import { runInAction } from "mobx";
import { APIClient } from "Application";
import toast from "react-hot-toast";
import moment from "moment";

import { AddStudentViewModel } from "../AddStudentViewModel";
import { GETStudentsEndpoint, StudentResponse } from "../EndPoints/GETStudentsEndpoint";
import { POSTMentorStudentEndPoint, POSTMentorStudentRequest } from "../EndPoints/POSTMentorStudentEndPoint";
import { WeeklyRotaDetailsViewModel } from "../../WeeklyRotaDetailViewModel";

export class APIManager {
    public apiClient = new APIClient();

    constructor(private viewModel: AddStudentViewModel) {}

    /** Loads the student list. Call from initialize(), not from constructor. */
    public async loadStudents(): Promise<void> {
        await this.apiClient.sendAsync(new GETStudentsEndpoint());
        if (this.apiClient.IsRequestSuccessful) {
            const responses = this.apiClient.Response<StudentResponse[]>() ?? [];
            runInAction(() => {
                if (responses.length > 0) {
                    this.viewModel.model.students.replace(responses);
                }
            });
        }
    }

    /**
     * Saves the mentor-student rota entry.
     * On success, refreshes the parent rota and closes the modal via onSuccess callback.
     */
    public async saveMentorStudent(parentViewModel: WeeklyRotaDetailsViewModel, onSuccess: () => void): Promise<void> {
        if (!this.viewModel.canSubmitForm) return;

        // Sync child model values to parent before posting
        runInAction(() => {
            parentViewModel.addStudentViewModel.model.id = this.viewModel.getValue("id");
            parentViewModel.addStudentViewModel.model.studentSessionId = this.viewModel.getValue("studentSessionId");
            parentViewModel.addStudentViewModel.model.selectedStudentId = this.viewModel.getValue("selectedStudentId");
            parentViewModel.addStudentViewModel.model.pickUpAddressId = this.viewModel.getValue("pickUpAddressId");
            parentViewModel.addStudentViewModel.model.dropOffAddressId = this.viewModel.getValue("dropOffAddressId");
            parentViewModel.addStudentViewModel.model.startDate = moment(parentViewModel.model.startDate).format("YYYY-MM-DD");
            parentViewModel.addStudentViewModel.model.startTime = this.viewModel.getValue("startTime");
            parentViewModel.addStudentViewModel.model.endTime = this.viewModel.getValue("endTime");
        });

        // Build request
        const model = parentViewModel.addStudentViewModel.model;
        const propertyKey = this.viewModel.managers.domain.buildSessionPropertyKey(model.startTime, model.studentSessionId || "");

        const request: POSTMentorStudentRequest = {
            id: model.id,
            rotaId: parentViewModel.model.rotaId,
            mentorId: model.mentorId,
            startDate: model.startDate,
            startTime: model.startTime,
            endTime: model.endTime,
            [propertyKey]: model.selectedStudentId,
        };
        if (model.pickUpAddressId !== "") request.pickUpAddressId = model.pickUpAddressId;
        if (model.dropOffAddressId !== "") request.dropOffAddressId = model.dropOffAddressId;

        await this.apiClient.sendAsync(new POSTMentorStudentEndPoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                parentViewModel.managers.mobx.setMentorStudentAdded(true);
                parentViewModel.managers.mobx.setSuccessfullyGetWeeklyRota(false);
                this.viewModel.model.reset();
            });
            await parentViewModel.managers.api.loadLeftStudentSession();
            await parentViewModel.managers.api.getRotaByDateAsync();
            onSuccess();
        } else {
            toast.error(this.apiClient.ValidationMessage, {
                position: "top-right",
                style: { marginTop: "45px" },
            });
        }
    }
}
